import { useState } from 'react';
import { Bell, ChevronDown, Check } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { supabase } from '@/lib/supabase';
import { LegalDocumentViewer } from '@/components/LegalDocumentViewer';
import { LEGAL_DOCUMENTS, type LegalDocument } from '@/legal';

/**
 * Карточка управления push-уведомлениями и согласием на рассылки
 */
export function NotificationSettingsCard() {
  const { profile, setProfile } = useUserStore();
  const { haptic } = useTelegram();
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<LegalDocument | null>(null);

  if (!profile) return null;

  const hasConsent = !!profile.newsletter_consent;

  function toggleExpand() {
    haptic('light');
    setExpanded(!expanded);
  }

  async function toggleConsent() {
    if (saving) return;
    haptic('medium');
    setSaving(true);
    try {
      const newConsent = !hasConsent;
      const { data, error } = await supabase
        .from('profiles')
        .update({
          newsletter_consent: newConsent,
          newsletter_consent_at: newConsent ? new Date().toISOString() : null,
        })
        .eq('id', profile!.id)
        .select()
        .single();
      
      if (error) throw error;
      setProfile(data);
      haptic('success');
    } catch (e) {
      console.error('[NotificationSettings] toggle error:', e);
      haptic('error');
    } finally {
      setSaving(false);
    }
  }

  function openNewsletterConsent() {
    haptic('light');
    setViewingDoc(LEGAL_DOCUMENTS['consent-newsletter']);
  }

  return (
    <>
      <div className="card overflow-hidden mb-5">
        <button
          onClick={toggleExpand}
          className="w-full p-3 flex items-center gap-3 hover:bg-bg-elevated/50 transition-colors"
        >
          <div className="w-9 h-9 rounded-xl bg-sky-glow/15 flex items-center justify-center shrink-0">
            <Bell size={16} className="text-sky-glow" />
          </div>
          <div className="flex-1 text-left">
            <div className="font-display font-semibold text-sm">Уведомления и рассылки</div>
            <div className="text-[11px] text-ink-dim">
              {hasConsent ? '✓ Получаешь напоминания' : 'Уведомления выключены'}
            </div>
          </div>
          <ChevronDown 
            size={16} 
            className={`text-ink-dim transition-transform ${expanded ? 'rotate-180' : ''}`} 
          />
        </button>

        {expanded && (
          <div className="px-3 pb-3 pt-1 border-t border-line/30 bg-bg-elevated/30">
            {/* Описание */}
            <p className="text-[11px] text-ink-muted leading-relaxed mt-2 mb-3">
              Push-уведомления приходят через бот @kokorineatbot:
            </p>

            <div className="space-y-1 mb-3 text-[11px] text-ink-muted">
              <div className="flex items-center gap-1.5">
                <span>🌅</span>
                <span>Утром (9:00) — напоминание о завтраке</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span>🍽️</span>
                <span>Днём (13:00) — напоминание об обеде</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span>🌙</span>
                <span>Вечером (21:00) — итоги дня</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span>👋</span>
                <span>Comeback — если давно не заходил</span>
              </div>
            </div>

            {/* Главная кнопка-чекбокс */}
            <button
              type="button"
              onClick={toggleConsent}
              disabled={saving}
              className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                hasConsent
                  ? 'bg-emerald-glow/10 border-emerald-glow/50'
                  : 'bg-bg border-line hover:border-emerald-glow/30'
              } ${saving ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start gap-2.5">
                <div className={`w-5 h-5 rounded-md shrink-0 mt-0.5 flex items-center justify-center transition-all ${
                  hasConsent
                    ? 'bg-emerald-glow text-bg'
                    : 'bg-bg-elevated border border-line'
                }`}>
                  {hasConsent && <Check size={14} strokeWidth={3} />}
                </div>
                <div className="text-[11px] text-ink leading-relaxed">
                  Я согласен получать push-уведомления и информационные рассылки от Slava Health
                </div>
              </div>
            </button>

            {/* Ссылка на согласие */}
            <button
              type="button"
              onClick={openNewsletterConsent}
              className="w-full mt-2 py-2 text-[11px] text-emerald-glow hover:underline text-center"
            >
              Прочитать «Согласие на рассылки» →
            </button>

            {/* Информация о согласии */}
            {hasConsent && profile.newsletter_consent_at && (
              <div className="mt-2 pt-2 border-t border-line/30">
                <p className="text-[10px] text-ink-dim text-center">
                  Согласие дано: {new Date(profile.newsletter_consent_at).toLocaleDateString('ru-RU')}
                </p>
              </div>
            )}

            {/* Подсказка как отключить полностью */}
            <p className="text-[10px] text-ink-dim leading-relaxed mt-3 pt-3 border-t border-line/30">
              💡 Чтобы вообще не получать сообщения от бота — отключи уведомления @kokorineatbot в настройках Telegram.
            </p>
          </div>
        )}
      </div>

      <LegalDocumentViewer
        document={viewingDoc}
        onClose={() => setViewingDoc(null)}
      />
    </>
  );
}
